import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { atom, useAtomValue, useSetAtom, useStore } from "jotai";
import { Suspense, useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import {
  ArrowLeftIcon,
  CloseIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { PoweredByWhooshStacked } from "@whoosh/design-system/src/components/icons_v2/special/PoweredByWhooshStacked";
import { Box, HStack } from "@whoosh/design-system/src/components/primitive";

import { LanguageSelectContainer } from "../../../../../LanguagePreference";
import { useAuthenticationLayoutLanguageSelect } from "../../../AuthenticationLayout";
import { GoBackToPreviousStepAtom } from "../atoms/actions";
import { VerificationModalStepStateAtom } from "../atoms/stores";
import { ClubLogo } from "./ClubLogo";
import { VerifiedAuthenticationModalHeaderQuery } from "./__generated__/VerifiedAuthenticationModalHeaderQuery.graphql";

const shouldCloseOnBackPressAtom = atom((get) => {
  const state = get(VerificationModalStepStateAtom);
  return state.backStep === null;
});

type VerifiedAuthenticationModalHeaderProps = {
  onClose?: () => void;
  clubSlug?: string;
  hideCloseButton?: boolean;
  languagePreferenceStore: ReturnType<typeof useStore>;
};

export const VerifiedAuthenticationModalHeader = ({
  hideCloseButton,
  languagePreferenceStore,
  onClose,
  clubSlug,
}: VerifiedAuthenticationModalHeaderProps) => {
  const showLanguageSelect = useAuthenticationLayoutLanguageSelect();
  const [queryRef, loadQuery] =
    useQueryLoader<VerifiedAuthenticationModalHeaderQuery>(
      VerifiedAuthenticationModalHeaderQueryString
    );

  useEffect(() => {
    if (clubSlug) {
      loadQuery({ clubSlug: clubSlug });
    }
  }, [loadQuery, clubSlug]);

  const shouldCloseOnBackPress = useAtomValue(shouldCloseOnBackPressAtom);
  const goBack = useSetAtom(GoBackToPreviousStepAtom);

  const handleButtonPress = () => {
    if (shouldCloseOnBackPress) {
      onClose?.();
    } else {
      goBack();
    }
  };

  return (
    <HStack
      mx={6}
      mt={6}
      pb={10}
      justifyContent={"space-between"}
      alignItems="center"
    >
      {hideCloseButton && shouldCloseOnBackPress ? (
        <Box />
      ) : (
        <Button
          accessibilityLabel={t`Back button`}
          onPress={handleButtonPress}
          variant="secondary"
          leftIcon={shouldCloseOnBackPress ? <CloseIcon /> : <ArrowLeftIcon />}
        />
      )}

      <HStack gap={3} alignItems="center">
        {showLanguageSelect ? (
          <LanguageSelectContainer
            store={languagePreferenceStore}
            testID="authentication-language-select"
          />
        ) : null}
        <PoweredByWhooshStacked width={89} height={31} />
        <Suspense
          fallback={
            <Box width={44} height={44}>
              <Loader />
            </Box>
          }
        >
          {queryRef ? (
            <VerifiedAuthenticationModalHeaderClubLogo queryRef={queryRef} />
          ) : null}
        </Suspense>
      </HStack>
    </HStack>
  );
};

const VerifiedAuthenticationModalHeaderClubLogo = ({
  queryRef,
}: {
  queryRef: PreloadedQuery<VerifiedAuthenticationModalHeaderQuery>;
}) => {
  const data = usePreloadedQuery(
    VerifiedAuthenticationModalHeaderQueryString,
    queryRef
  );

  return data.unauthenticatedClub ? (
    <ClubLogo clubRef={data.unauthenticatedClub} />
  ) : null;
};

export const VerifiedAuthenticationModalHeaderQueryString = graphql`
  query VerifiedAuthenticationModalHeaderQuery($clubSlug: String!) {
    unauthenticatedClub(slug: $clubSlug) {
      ...ClubLogoFragment_UnauthenticatedClub
    }
  }
`;
